import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, FileText, MessageSquare, User, LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/public/Brand";
import { ThemeToggle } from "@/components/public/PublicLayout";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/orders", label: "My Orders", icon: ShoppingBag },
  { to: "/portal/requests", label: "My Requests", icon: FileText },
  { to: "/portal/complaints", label: "Complaints", icon: MessageSquare },
  { to: "/portal/profile", label: "Profile", icon: User },
];

export default function PortalLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const active = (n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 glass">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <Brand height={34} textClass="hidden font-display font-extrabold sm:block" />
          </Link>
          <span className="ml-2 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">Customer Portal</span>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="grid h-9 w-9 place-items-center rounded-full border border-border hover:bg-secondary" title="Store"><Home className="h-4 w-4" /></Link>
            <ThemeToggle />
            <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout" className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-secondary"><LogOut className="h-4 w-4" />Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav className="sticky top-20 grid gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`portal-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${active(n) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </Link>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1">
          {title && <h1 className="mb-5 font-display text-2xl font-bold tracking-tight">{title}</h1>}
          {children}
          <div className="mt-6 flex gap-2 overflow-x-auto md:hidden">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${active(n) ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}><n.icon className="h-3.5 w-3.5" />{n.label}</Link>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
